import React, { FC, useCallback, useContext, useState } from 'react';
import { 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Checkbox, 
  FormControlLabel, 
  Alert,
  Link
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../../../../context/auth';
import { useCheckPinMutation } from './mutation';
import { useTranslation } from 'react-i18next';
import { useUserQuery } from '../../../../hooks/query';
import { takeContractId } from '../../../../utils/refetch';
import BaseModal from '../BaseModal/BaseModal';

type Props = {
  location: string;
  balance: number | string;
  price: number;
  centered: boolean;
  show: boolean;
  selectedButton: string;
  onHide: () => void;
  handleSuccess: () => void;
  rent?: boolean;
  streamLimitReached?: boolean;
};

const OrderModal: FC<Props> = ({
  location,
  balance,
  price,
  show,
  onHide,
  handleSuccess,
  selectedButton,
  rent,
  streamLimitReached,
}) => {
  const [error, setError] = useState<string>('');
  const authContext = useContext(AuthContext);
  const { userLocation } = authContext;
  const [checkbox1, setCheckbox1] = useState<boolean>(false);
  const [checkbox2, setCheckbox2] = useState<boolean>(false);
  const [checkbox3, setCheckbox3] = useState<boolean>(false);
  const [errors, setErrors] = useState<boolean>(false);
  const navigate = useNavigate();
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const { t } = useTranslation();
  let { id } = useParams<{ id: string }>();

  const [pin, setPin] = useState('');
  useUserQuery(true, takeContractId() != null);
  const { mutate, isLoading } = useCheckPinMutation();

  const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && value.length <= 4) {
      setPin(value);
    }
  };

  const handleNext = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!checkbox1 || !checkbox2 || !checkbox3) {
      setErrors(true);
      return;
    } else if (pin?.length < 4) {
      return;
    }

    try {
      mutate(
        {
          pin: pin,
        },
        {
          onSuccess(data: any) {
            setIsDisabled(true);
            if (data?.status === 'success') {
              if (typeof balance === 'number' ? balance < price : parseFloat(balance) < price) {
                navigate(`/de/deposit/${id}`, {
                  state: {
                    prevPath: location,
                  },
                });
              } else {
                handleSuccess();
              }
            }
          },
          onError(error: any) {
            setError(error?.body?.message || 'Ein Fehler ist aufgetreten');
          },
        },
      );
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleHide = () => {
    setCheckbox1(false);
    setCheckbox2(false);
    setCheckbox3(false);
    setErrors(false);
    setPin('');
    setError('');
    setIsDisabled(false);
    onHide();
  };

  const isInvalid = useCallback(() => {
    if (pin?.length < 4 || !checkbox1 || !checkbox2 || !checkbox3 || isLoading) {
      return true;
    }
    return false;
  }, [checkbox1, checkbox2, checkbox3, pin?.length, isLoading]);

  const calculateVat = (price: number, userLocation: string): string => {
    if (userLocation === 'AT') {
      if (price === 9) return '1,50 €';
      if (price === 12) return '2,00 €';
      if (price === 16) return '2,67 €';
      if (price === 12.80) return ' 2,13 €';
      if (price === 5.40) return ' 0,90 €';
      if (price === 7.20) return ' 1,20 €';
      if (price === 9.60) return ' 1,60 €';
    } else if (userLocation === 'DE') {
      if (price === 9) return '1,44 €';
      if (price === 12) return '1,92 €';
      if (price === 16) return '2,55 €';
      if (price === 5.40) return ' 0,86 €';
      if (price === 12.80) return ' 2,04 €';
      if (price === 7.20) return ' 1,15 €';
      if (price === 9.60) return ' 1,53 €';
    }
    return '1,33 €';
  };

  return (
    <BaseModal
      open={show}
      onClose={handleHide}
      title="FILM BESTELLEN"
      sx={{ '& .MuiDialog-paper': { width: '625px', maxWidth: '625px' } }}
      actions={
        <Box display="flex" gap={2} justifyContent="center" width="100%">
          <Button 
            variant="contained" 
            onClick={handleHide}
            size="large"
            className="danger-button"
            fullWidth
            sx={{ color: 'white' }}
          >
            ABBRECHEN
          </Button>
          <Button 
            variant="contained" 
            disabled={isInvalid() || isDisabled} 
            onClick={handleNext}
            size="large"
            className="primary"
            fullWidth
            sx={{ color: 'white' }}
          >
            KOSTENPFLICHTIG BESTELLEN
          </Button>
        </Box>
      }
    >
      <Box sx={{ mt: 0 }}>
        {/* Price Information */}
        <Box sx={{ mb: 3, mt: 0, textAlign: 'center' }}>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, color: '#4A4A4A' }}>
              Preis:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#5670C9', fontSize: '16px' }}>
              {price.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
            </Typography>
          </Box>
          
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, color: '#4A4A4A' }}>
              inkl. MwSt.:
            </Typography>
            <Typography variant="body2" sx={{fontWeight: 'bold', color: '#5670C9', fontSize: '16px' }}>
              {calculateVat(price, userLocation)}
            </Typography>
          </Box>
          
          <Box>
            <Typography variant="body2" sx={{  mb: 1, color: '#4A4A4A' }}>
              Freischaltung:
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#4A4A4A' }}>
              {rent
                ? 'Sie erwerben das Recht diesen Film für 48 Stunden erneut ohne weitere Kosten im Stream anzuschauen.'
                : 'Sie erwerben das Recht diesen Film für 48 Stunden erneut ohne weitere Kosten herunterzuladen'}
            </Typography>
          </Box>
        </Box>

        {/* Terms and Conditions */}
        <Box
          sx={{
            mb: 3,
            p: 0,
            '& .MuiFormControlLabel-root': {
              alignItems: 'flex-start',
              mb: 1.5,
            },
            '& .MuiFormControlLabel-root:last-of-type': {
              mb: 0,
            },
            '& .MuiTypography-root': {
              m: 0,
            },
            '& .MuiCheckbox-root': {
              pt: 0,
              pb: 0
            },
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={checkbox1}
                onChange={(e) => setCheckbox1(e.target.checked)}
                name="agb"
                color="secondary"
                sx={{
                  color: '#A1A1A1',
                  '&.Mui-checked': { color: '#5670C9' },
                  '& .MuiSvgIcon-root': {
                    borderRadius: '4px',
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: '#4A4A4A' }}>
                Ich habe die{' '}
                <Link href="/de/agb" target="_blank" sx={{ color: '#5670C9', fontWeight: 'bold' }}>
                  AGB
                </Link>{' '}
                gelesen und stimme diesen zu.
              </Typography>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={checkbox2}
                onChange={(e) => setCheckbox2(e.target.checked)}
                name="cancellation-terms"
                color="secondary"
                sx={{
                  color: '#A1A1A1',
                  '&.Mui-checked': { color: '#5670C9' },
                  '& .MuiSvgIcon-root': {
                    borderRadius: '4px',
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: '#4A4A4A' }}>
                Ich nehme die{' '}
                <Link
                  href="/de/cancellation-terms"
                  target="_blank"
                  underline="always"
                  sx={{ color: '#5670C9', fontWeight: 'bold' }}
                >
                  Widerrufsbelehrung
                </Link>{' '}
                zur Kenntnis.
                <br />
                Ich stimme zu, dass tmc Media Group AG bei Bestellungen mit Programmstart vor Ablauf der
                14tägigen Widerrufsfrist zum Startzeitpunkt mit der Ausführung beginnt. Mit Beginn des Vertrages
                verliere ich mein Widerrufsrecht.
              </Typography>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={checkbox3}
                onChange={(e) => setCheckbox3(e.target.checked)}
                name="tmc-abo"
                color="secondary"
                sx={{
                  color: '#A1A1A1',
                  '&.Mui-checked': { color: '#5670C9' },
                  '& .MuiSvgIcon-root': {
                    borderRadius: '4px',
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: '#4A4A4A' }}>
                Sollte die tmc das Abonnement (Monats- und Jahresticket) zu einem späteren Zeitpunkt mit allen
                Rechten und Pflichten auf die Sky Deutschland Fernsehen GmbH & Co. KG, Medienallee 26, 85774
                Unterföhring („Sky") übertragen, stimme ich bereits jetzt hiermit dieser Vertragsübertragung zu.
              </Typography>
            }
          />
        </Box>

        {/* Error Messages */}
        {errors && (!checkbox1 || !checkbox2 || !checkbox3) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <div>
              {!checkbox1 && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Bitte bestätigen Sie, dass Sie die AGB gelesen und zugestimmt haben.
                </Typography>
              )}
              {!checkbox2 && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Bitte bestätigen Sie, dass Sie die Widerrufsbelehrung zur Kenntnis genommen haben.
                </Typography>
              )}
              {!checkbox3 && (
                <Typography variant="body2">
                  Bitte bestätigen Sie, dass Sie die Abonnement Übertragung zur Kenntnis genommen haben.
                </Typography>
              )}
            </div>
          </Alert>
        )}

        {/* Content Warning */}
        {(selectedButton === 'hd' || selectedButton === 'sd') && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: '#4A4A4A' }}>
              Der Inhalt ist nach dem Download nicht zugriffsbeschränkt. Er darf Minderjährigen unter keinen
              Umständen zugänglich gemacht werden.
            </Typography>
          </Alert>
        )}

        {/* PIN Input */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Um diesen Film ansehen zu können, müssen Sie hier Ihre {userLocation === 'DE' ? '18+' : 'Sky'} PIN
            eingeben.
          </Typography>
          
          <TextField
            name="pin"
            label={userLocation === 'DE' ? '18+ PIN' : 'Sky PIN'}
            slotProps={{ 
              input: {
                inputProps: {
                  maxLength: 4
                }
              }
            }}
            value={pin}
            onChange={handlePinChange}
            type="password"
            required
            autoFocus
            focused
            fullWidth
            variant="outlined"
            margin="dense"
            error={pin.length > 0 && pin.length < 4}
            helperText={pin.length > 0 && pin.length < 4 ? 'PIN muss 4 Ziffern haben' : ''}
            sx={{ 
              maxWidth: 'none !important',
              width: '100%',
              '&.MuiTextField-root': {
                maxWidth: 'none !important'
              }
            }}
          />
        </Box>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {String(error)}
          </Alert>
        )}

        {/* Stream Limit Reached */}
        {streamLimitReached && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {t('orderModal.limitReached')}
            </Typography>
            <Typography variant="body2">
              {t('orderModal.limitReachedText')}
            </Typography>
          </Alert>
        )}
      </Box>
    </BaseModal>
  );
};

export default OrderModal;